import React, { useState, useCallback } from 'react';
import { Upload, Image, Camera, X, Check, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { customCategories } from '../data/mock';

const CustomizeSection = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImage = () => {
    setUploadedImage(null);
  };

  return (
    <section id="customize" className="relative py-20 lg:py-28 bg-gradient-to-b from-[#252020] to-[#1e1919]">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #ffd700 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#61525a]/20 backdrop-blur-sm border border-[#61525a]/30 rounded-full px-4 py-2 mb-6">
            <Camera className="w-4 h-4 text-[#ffd700]" />
            <span className="text-white/80 font-medium text-sm">Start Creating</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Upload & <span className="text-[#00d4aa]">Customize</span>
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Select a product category, upload your image, and create something truly unique. It's that simple!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left - Category Selection */}
          <div>
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-[#ffd700] text-[#1e1919] flex items-center justify-center text-sm font-bold">1</span>
              Select Product Type
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {customCategories.slice(0, 8).map((category) => (
                <Card
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`cursor-pointer transition-all duration-300 ${
                    selectedCategory === category.id
                      ? 'bg-[#ffd700]/20 border-[#ffd700]'
                      : 'bg-white/5 border-white/10 hover:border-white/30'
                  }`}
                >
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                      selectedCategory === category.id
                        ? 'bg-[#ffd700] text-[#1e1919]'
                        : 'bg-white/10 text-white'
                    }`}>
                      {selectedCategory === category.id ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <Image className="w-5 h-5" />
                      )}
                    </div>
                    <span className={`font-medium text-sm ${
                      selectedCategory === category.id ? 'text-[#ffd700]' : 'text-white/80'
                    }`}>
                      {category.name}
                    </span>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Right - Upload Area */}
          <div>
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-[#00d4aa] text-[#1e1919] flex items-center justify-center text-sm font-bold">2</span>
              Upload Your Image
            </h3>
            
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-3xl transition-all duration-300 overflow-hidden ${
                isDragging
                  ? 'border-[#00d4aa] bg-[#00d4aa]/10'
                  : uploadedImage
                  ? 'border-[#ffd700] bg-[#ffd700]/5'
                  : 'border-white/20 hover:border-white/40 bg-white/5'
              }`}
            >
              {uploadedImage ? (
                <div className="relative aspect-square">
                  <img
                    src={uploadedImage}
                    alt="Uploaded"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1e1919] via-transparent to-transparent" />
                  <Button
                    onClick={clearImage}
                    size="icon"
                    variant="ghost"
                    className="absolute top-4 right-4 bg-[#1e1919]/80 hover:bg-[#ff6b6b] text-white rounded-full"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-white font-medium mb-2">Image uploaded successfully!</p>
                    <Button className="w-full bg-gradient-to-r from-[#ffd700] to-[#ffb347] hover:from-[#ffcc00] hover:to-[#ffa500] text-[#1e1919] font-bold">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Preview & Customize
                    </Button>
                  </div>
                </div>
              ) : (
                <label className="cursor-pointer block p-12">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <div className="text-center">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#ffd700]/20 to-[#00d4aa]/20 flex items-center justify-center">
                      <Upload className="w-10 h-10 text-[#ffd700]" />
                    </div>
                    <h4 className="text-xl font-bold text-white mb-2">Drag & Drop Your Image</h4>
                    <p className="text-white/50 mb-4">or click to browse from your device</p>
                    <div className="flex items-center justify-center gap-4 text-sm text-white/40">
                      <span>JPG</span>
                      <span>•</span>
                      <span>PNG</span>
                      <span>•</span>
                      <span>WEBP</span>
                      <span>•</span>
                      <span>Up to 10MB</span>
                    </div>
                  </div>
                </label>
              )}
            </div>

            {/* Process Steps */}
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                { step: 1, label: 'Upload', icon: Upload, done: !!uploadedImage },
                { step: 2, label: 'Customize', icon: Sparkles, done: false },
                { step: 3, label: 'Order', icon: Check, done: false },
              ].map((item, index) => (
                <div key={item.step} className="text-center">
                  <div className={`w-12 h-12 mx-auto mb-2 rounded-full flex items-center justify-center transition-colors ${
                    item.done
                      ? 'bg-[#00d4aa] text-[#1e1919]'
                      : 'bg-white/10 text-white/50'
                  }`}>
                    <item.icon className="w-5 h-5" />
                  </div>
                  <p className={`text-sm font-medium ${item.done ? 'text-[#00d4aa]' : 'text-white/50'}`}>
                    {item.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomizeSection;
